'use client';

import { Box, Menu, MenuItem, Popover, Stack, Typography } from '@mui/material';
import { MuiButton } from '@/components/button/MuiButton';
import { useState } from 'react';
import { MuiIconButton } from '@/components/button/MuiIconButton';
import { Menu as MenuIcon } from 'mdi-material-ui';
import { usePathname } from 'next/navigation';
import { Route } from 'next';
import { pagesPath } from '@/lib/$path';
import LockIcon from '@mui/icons-material/Lock';
import { RequireLoginContent } from '@/features/hospital/HospitalSearchFilters/PopoverContentContainer/RequireLoginContent';
import { useNextRouter } from '@/hooks/useNextRouter';

type Props = {
  isPC: boolean;
};

type PublicMenuListType = (
  | {
      disabled: false;
      url: string;
      title: string;
      selectedUrl: string | RegExp;
    }
  | {
      disabled: true;
      title: string;
    }
)[];

const PublicMenuList: PublicMenuListType = [
  {
    disabled: false,
    url: pagesPath.public.$url().path,
    title: 'ホーム',
    selectedUrl: /^\/public$/,
  },
  {
    disabled: false,
    url: pagesPath.public.stations.search.$url().path,
    title: '検索',
    selectedUrl: pagesPath.public.stations.search.$url().path,
  },
  {
    disabled: true,
    title: '紹介カード',
  },
  {
    disabled: true,
    title: '利用者管理',
  },
  {
    disabled: true,
    title: '設定',
  },
];

export function PublicMenuBar({ isPC }: Props) {
  const router = useNextRouter();
  const pathname = usePathname();

  const onClickMenu = (url: string) => {
    router.push(url as Route);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [anchorElDisabledMenu, setAnchorElDisabledMenu] =
    useState<HTMLButtonElement | null>(null);

  const handleClickDisabledMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setAnchorElDisabledMenu(event.currentTarget);
    event.stopPropagation();
    event.preventDefault();
  };

  const handleCloseDisabledMenu = () => {
    setAnchorElDisabledMenu(null);
  };

  const [anchorElSPDisabledMenu, setAnchorElSPDisabledMenu] =
    useState<HTMLLIElement | null>(null);

  const handleClickSPDisabledMenu = (
    event: React.MouseEvent<HTMLLIElement>,
  ) => {
    setAnchorElSPDisabledMenu(event.currentTarget);
    event.stopPropagation();
    event.preventDefault();
  };

  const handleCloseSPDisabledMenu = () => {
    setAnchorElSPDisabledMenu(null);
  };

  if (!isPC) {
    return (
      <Box
        sx={{
          display: 'none',
          '@media (max-width: 900px)': {
            display: 'block',
          },
        }}
      >
        <MuiIconButton
          id="sp-menu-button"
          aria-controls={open ? 'admin-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          color="inherit"
          sx={{ p: 0 }}
        >
          <MenuIcon />
        </MuiIconButton>
        <Menu
          id="sp-menu"
          anchorEl={anchorEl}
          open={open}
          onClick={handleClose}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'sp-menu-button',
          }}
        >
          {PublicMenuList.map((menu, index) =>
            menu.disabled ? (
              <MenuItem
                key={`public-menu-${index}`}
                onClick={handleClickSPDisabledMenu}
                sx={{
                  '&.MuiMenuItem-root': {
                    color: 'text.disabled',
                  },
                  cursor: 'not-allowed',
                }}
              >
                <LockIcon
                  sx={{
                    color: '#FFB300',
                    mr: 0.5,
                  }}
                />
                {menu.title}
              </MenuItem>
            ) : (
              <MenuItem
                key={`public-menu-${index}`}
                selected={!!pathname.match(menu.selectedUrl)}
                onClick={() => onClickMenu(menu.url)}
                sx={{
                  '&.MuiMenuItem-root': {
                    color: !!pathname.match(menu.selectedUrl)
                      ? 'primary.main'
                      : 'inherit',
                  },
                }}
              >
                {menu.title}
              </MenuItem>
            ),
          )}
        </Menu>
        <Popover
          open={!!anchorElSPDisabledMenu}
          anchorEl={anchorElSPDisabledMenu}
          onClose={handleCloseSPDisabledMenu}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
        >
          <RequireLoginContent />
        </Popover>
      </Box>
    );
  }

  return (
    <>
      <Stack
        component="ul"
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
          display: 'block',
          height: '100%',
          marginBlockStart: 0,
          marginBlockEnd: 0,
          paddingInlineStart: 0,
          '@media (max-width: 900px)': {
            display: 'none',
          },
        }}
      >
        {PublicMenuList.map((menu, index) =>
          menu.disabled ? (
            <MuiButton
              key={`public-menu-${index}`}
              variant="text"
              component="li"
              sx={{
                listStyle: 'none',
                height: '64px',
                borderRadius: 0,
                borderBottom: 'none',
                color: 'text.disabled',
                cursor: 'not-allowed',
              }}
              onClick={handleClickDisabledMenu}
            >
              <LockIcon
                sx={{
                  color: '#FFB300',
                  mr: 0.5,
                }}
              />
              <Typography variant="JP/Body/m/Medium/Tight">
                {menu.title}
              </Typography>
            </MuiButton>
          ) : (
            <MuiButton
              key={`public-menu-${index}`}
              variant="text"
              component="li"
              sx={{
                listStyle: 'none',
                color: 'inherit',
                height: '64px',
                borderRadius: 0,
                borderBottom: !!pathname.match(menu.selectedUrl)
                  ? '2px solid #FFD600'
                  : 'none',
              }}
              onClick={() => onClickMenu(menu.url)}
            >
              <Typography variant="JP/Body/m/Medium/Tight">
                {menu.title}
              </Typography>
            </MuiButton>
          ),
        )}
      </Stack>
      <Popover
        open={!!anchorElDisabledMenu}
        anchorEl={anchorElDisabledMenu}
        onClose={handleCloseDisabledMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <RequireLoginContent />
      </Popover>
    </>
  );
}
